<template>
  <mu-card class="reg-form">
    <mu-card-header :title="$t('message.labelLetsGetStarted')"
                    titleClass="regTitle"></mu-card-header>
    <mu-card-text>
      <p v-if="!$route.query.hcp">Enter the basics to begin personalising your Low Carb Program experience. If
        you already have an account, <span v-if="model.offerCode"> <router-link :to="`/login?oc=${model.offerCode}`">click here to log in</router-link></span>
        <span v-else>
                  <router-link to="/login">click here to log in</router-link>
                </span>
      </p>
      <div class="buttonWrap">
        <mu-flexbox orient="vertical">
          <mu-flexbox-item v-for="(provider, index) in providers" :key="`provider-${index}`">
            <mu-raised-button v-on:click="connectAccount(provider.connectUrl)"
                              type="button"
                              full-width
                              :label="provider.buttonText && provider.buttonText.length ? provider.title.toString().replace(/^/, 'Register using ') : ''"
                              class="loginButton" v-bind:style="{backgroundColor:provider.brandColour, backgroundImage: provider.buttonText ? '' : `url(${provider.image})`}"
                              v-bind:class="{darkTextButton : provider.brandColour === '#ffffff', buttonImageOnly : !provider.buttonText || !provider.buttonText.length} "
                              v-bind:disabled="isLoading">
              <img style="cursor: pointer" v-bind:src="provider.image">
            </mu-raised-button>
          </mu-flexbox-item>
        </mu-flexbox>
      </div>
      <p v-if="$route.query.hcp">Join the free Healthcare Professional Platform</p>
      <form v-on:submit.prevent="submitForm">

        <mu-flexbox align="baseline" style="margin-bottom: 8px;">
          <mu-flexbox-item>
            <mu-text-field
              id="firstnameTxt"
              v-validate="{required:true, max: 40, regex: /^[a-zA-Z ]*$/}"
              data-vv-name="first_name"
              data-vv-value-path="inputValue"
              data-vv-validate-on="none"
              :data-vv-as="$t('message.accountPreferencesFirstName')"
              :errorText="errors.first('first_name')"
              :label="$t('message.labelYourName')"
              type="text"
              v-model="model.firstname"
              :hintText="$t('message.accountPreferencesFirstName')"
              fullWidth>
            </mu-text-field>
          </mu-flexbox-item>
          <mu-flexbox-item>
            <mu-text-field
              id="surnameTxt"
              label=""
              v-validate="{required:true, max: 40, regex: /^[a-zA-Z ]*$/}"
              data-vv-name="last_name"
              data-vv-value-path="inputValue"
              data-vv-validate-on="none"
              :data-vv-as="$t('message.accountPreferencesSurname')"
              :errorText="errors.first('last_name')"
              type="text"
              v-model="model.surname"
              :hintText="$t('message.accountPreferencesSurname')"
              fullWidth>
            </mu-text-field>
          </mu-flexbox-item>
        </mu-flexbox>

        <mu-text-field
          id="emailTxt"
          v-validate="'required|email'"
          data-vv-name="email"
          data-vv-value-path="inputValue"
          data-vv-validate-on="none"
          :data-vv-as="$t('message.accountPreferencesEmail')"
          :errorText="errors.first('email')"
          :label="$t('message.accountPreferencesEmail')"
          type="email"
          v-model="model.email"
          :hintText="$t('message.placeholderEmail')"
          fullWidth
        ></mu-text-field>

        <mu-text-field
          id="emailTxtV"
          v-validate="'required|email'"
          data-vv-name="emailVerify"
          data-vv-value-path="inputValue"
          data-vv-validate-on="none"
          :data-vv-as="$t('message.accountPreferencesEmailVerify')"
          :errorText="errors.first('emailVerify')"
          :label="$t('message.accountPreferencesEmailVerify')"
          type="email"
          v-model="model.emailVerify"
          :hintText="$t('message.placeholderEmail')"
          fullWidth
        ></mu-text-field>

        <mu-row gutter>
          <mu-col width="100" tablet="50" desktop="50">
            <div class="field">
              <mu-text-field
                id="passwordTxt"
                :type="model.visible === true ? 'text' : 'password'"
                v-validate
                ref="password"
                :data-vv-rules="'required'"
                data-vv-name="password"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.accountPreferencesPassword')"
                :label="$t('message.accountPreferencesPassword')"
                v-model="model.password"
                :hintText="$t('message.accountPreferencesPassword')"
                :errorText="errors.first('password')"
                fullWidth
                class="pw-field">
              </mu-text-field>
              <mu-icon-button
                class="right-icon"
                @click.native="model.visible = !model.visible"
                :icon="!model.visible ? 'visibility' : 'visibility_off'"
              ></mu-icon-button>
            </div>
          </mu-col>
        </mu-row>
        <password-limit-validation :password="model.password"/>

        <password-strength-validation :password="model.password"/>

        <mu-row gutter>
          <mu-col width="100" tablet="50" desktop="50">
            <div class="field">
              <mu-text-field
                id="passwordTxtV"
                :type="model.verifyVisible === true ? 'text' : 'password'"
                v-validate
                :data-vv-rules="'required|confirmed:password'"
                data-vv-name="passwordVerify"
                data-vv-value-path="inputValue"
                :data-vv-as="$t('message.accountPreferencesPasswordVerify')"
                :label="$t('message.accountPreferencesPasswordVerify')"
                v-model="model.passwordVerify"
                :hintText="$t('message.accountPreferencesPasswordVerify')"
                :errorText="confirmPassErr"
                fullWidth>
              </mu-text-field>
              <mu-icon-button
                class="right-icon"
                @click.native="model.verifyVisible = !model.verifyVisible"
                :icon="!model.verifyVisible ? 'visibility': 'visibility_off'"
              ></mu-icon-button>
            </div>
          </mu-col>
        </mu-row>


        <div class="mu-text-field-label">
          Date of birth
        </div>

        <mu-row>
          <mu-col desktop="33" tablet="33" width="33">
            <select
              name="birth_day"
              v-validate
              :class="{'error': errors.first('birth_day')}"
              :data-vv-rules="'required'"
              data-vv-name="birth_day"
              data-vv-as="day"
              v-model="dob.day" class="native-select"
            >
              <option :value="null">Day</option>
              <option
                v-for="(day, index) in dobItems.days"
                :key="'day-' + index"
                :value="day"
              >{{day}}
              </option>
            </select>
          </mu-col>
          <mu-col desktop="33" tablet="33" width="33">
            <select
              name="birth_month"
              v-validate
              :class="{'error': errors.first('birth_month')}"
              :data-vv-rules="'required'"
              data-vv-name="birth_month"
              data-vv-as="month"
              v-model="dob.month" class="native-select">
              <option :value="null">Month</option>
              <option
                v-for="(month, index) in dobItems.months"
                v-once
                :key="'month-' + index"
                :value="month"
              >{{localizeMonth(month.name)}}
              </option>
            </select>
          </mu-col>
          <mu-col desktop="33" tablet="33" width="33">
            <select
              name="birth_year"
              v-validate
              :class="{'error': errors.first('birth_year')}"
              :data-vv-rules="'required'"
              data-vv-name="birth_year"
              data-vv-as="year"
              v-model="dob.year"
              class="native-select">
              <option :value="null">Year</option>
              <option
                v-for="(year, index) in dobItems.years"
                v-once
                :key="'year-' + index"
                :value="year"
              >{{year.value}}
              </option>
            </select>
          </mu-col>
        </mu-row>
        <p class="privacyDisclaimer" @click="setTerms">
          <mu-checkbox v-model="model.terms"
          >
          </mu-checkbox>
          {{$t('message.registerTermsByContinuing')}}
          <a :href="`${siteUrl}terms`" target="_blank">{{$t('message.registerTermsAndCondtions')}}</a>
        </p>
        <p class="privacyDisclaimer" @click="setPrivacyPolicy">
          <mu-checkbox v-model="model.privacyPolicy">
          </mu-checkbox>
          {{$t('message.registerTermsByContinuing')}}

          <a :href="`${siteUrl}privacy`" target="_blank">{{$t('message.registerPrivacyPolicy')}}</a>
          including
          <a :href="`${siteUrl}cookies`" target="_blank">{{$t('message.registerIncludingCookie')}}</a>
        </p>
        <div class="field">
          <VueRecaptcha
            :sitekey="captchaSiteKey"
            :loadRecaptchaScript="false"
            ref="recaptcha"
            @verify="onCaptchaVerified"
            @expired="onCaptchaExpired"></VueRecaptcha>
        </div>


        <button type="submit" style="display: none;"></button>

        <mu-raised-button
          id="submitBtn"
          @click.native="submitForm()"
          type="button"
          :label="isSaving ? $t('message.labelProcessing') : $t('message.registerAppBarText')"
          :disabled="isSaving"
          secondary
          fullWidth
        ></mu-raised-button>
      </form>
      <div v-if="isServerValidationError">
        {{serverValidationErrorText}}
      </div>
      <transition name="fade">
        <alert type="danger" icon="error" v-if="isError" style="margin-top: 16px; margin-bottom: 0px;">
          <div slot="content">
            {{ $t('message.registerErrorUserExists') }}
          </div>
        </alert>
        <alert type="danger" icon="error" v-if="isTermsError" style="margin-top: 24px; margin-bottom: 0px;">
          <div slot="content">
            Please agree to our terms and conditions
          </div>
        </alert>
        <alert type="danger" icon="error" v-if="isPrivacyPolicyError" style="margin-top: 24px; margin-bottom: 0px;">
          <div slot="content">
            Please agree to our privacy policy
          </div>
        </alert>
        <alert type="danger" icon="error" v-if="isServerValidationError"
               style="margin-top: 16px; margin-bottom: 0px;">
          <div slot="content">
            {{serverValidationErrorText}}
          </div>
        </alert>
      </transition>
    </mu-card-text>
  </mu-card>
</template>

<script>
  import MonthLocalizerMixin from "../../mixins/Localization/DateMonths";
  import SocialLoginMixin from "../../mixins/SocialLoginMixin";
  import RegistrationMixin from "../../mixins/RegistrationMixin";
  import PasswordStrengthValidation from "../PasswordStrengthValidation/PasswordStrengthValidation";
  import PasswordLimitValidation from "../PasswordLimitValidation/PasswordLimitValidation";
  import VueRecaptcha  from 'vue-recaptcha';

  import axios from "axios";

  export default {
    name: "InitialRegistration",
    mixins: [MonthLocalizerMixin, SocialLoginMixin, RegistrationMixin],
    watch: {
      '$route': {
        handler: function () {
          if (_.has(this.$route.query, 'oc')) {
            this.model.offerCode = this.$route.query.oc
          }
          if (_.has(this.$route.query, 'rid')) {
            this.model.referralId = this.$route.query.rid
          }
          if (_.has(this.$route.query, 'sid')) {
            this.model.subReferralId = this.$route.query.sid
          }
        },
        immediate: true
      },
      'model.captchaFilled' : {
        handler: function(val) {
          if(val === false) this.onCaptchaExpired() // reset the captcha anytime the status is changed to false.
        }
      }
    },
    components: {
      PasswordStrengthValidation,
      PasswordLimitValidation,
      VueRecaptcha
    },
    computed: {
      confirmPassErr: function () {
        let self = this;
        let error = self.errors.first('passwordVerify')
        if (error === "The Confirm Password confirmation does not match.") {
          return 'Please enter passwords that match';
        } else {
          return error;
        }
      }
    },
    methods: {
      onCaptchaVerified: function (recaptchaToken) {
        this.model.captcha = recaptchaToken;
        this.model.captchaFilled = true
      },
      onCaptchaExpired: function () {
        this.$refs.recaptcha.reset();
        this.model.captcha = '';
        this.model.captchaFilled = false
      }
    }
  }
</script>



// WEBPACK FOOTER //
// InitialRegistration.vue?792b789f